/**
 * Voice Narration - Text-to-speech for poker coach and game events
 */

class VoiceNarrator {
  constructor() {
    this.enabled = false;
    this.speaking = false;
    this.volume = 0.8;
    this.rate = 1.0;
    this.voice = null;
    this.queue = [];
    this.synthesis = typeof window !== 'undefined' ? window.speechSynthesis : null;
    
    // Load preferences from localStorage
    this.loadPreferences();
    
    // Initialize voice when voices are loaded
    if (this.synthesis) {
      this.synthesis.onvoiceschanged = () => this.selectBestVoice();
      this.selectBestVoice();
    }
  }
  
  loadPreferences() {
    try {
      const prefs = localStorage.getItem('boosty_voice_prefs');
      if (prefs) {
        const { enabled, volume, rate } = JSON.parse(prefs);
        this.enabled = enabled ?? false;
        this.volume = volume ?? 0.8;
        this.rate = rate ?? 1.0;
      }
    } catch (e) {
      console.error('Failed to load voice preferences:', e);
    }
  }
  
  savePreferences() {
    try {
      localStorage.setItem('boosty_voice_prefs', JSON.stringify({
        enabled: this.enabled,
        volume: this.volume,
        rate: this.rate
      }));
    } catch (e) {
      console.error('Failed to save voice preferences:', e);
    }
  }
  
  selectBestVoice() {
    if (!this.synthesis) return;
    
    const voices = this.synthesis.getVoices();
    
    // Prefer English voices
    const englishVoices = voices.filter(v => v.lang.startsWith('en'));
    
    // Prefer female voices for a friendly tone
    const preferredVoice = englishVoices.find(v => 
      v.name.toLowerCase().includes('samantha') ||
      v.name.toLowerCase().includes('victoria') ||
      v.name.toLowerCase().includes('karen') ||
      v.name.toLowerCase().includes('female')
    ) || englishVoices[0] || voices[0];
    
    this.voice = preferredVoice;
  }
  
  setEnabled(enabled) {
    this.enabled = enabled;
    this.savePreferences();
    
    if (!enabled && this.synthesis) {
      this.synthesis.cancel();
      this.queue = [];
    }
  }
  
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    this.savePreferences();
  }
  
  setRate(rate) {
    this.rate = Math.max(0.5, Math.min(2, rate));
    this.savePreferences();
  }
  
  speak(text, priority = 'normal') {
    if (!this.enabled || !this.synthesis || !text) return;
    
    // High priority interrupts current speech
    if (priority === 'high') {
      this.synthesis.cancel();
      this.queue = [];
    }
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.voice = this.voice;
    utterance.volume = this.volume;
    utterance.rate = this.rate;
    utterance.pitch = 1.0;
    
    utterance.onstart = () => {
      this.speaking = true;
    };
    
    utterance.onend = () => {
      this.speaking = false;
      this.processQueue();
    };
    
    utterance.onerror = (e) => {
      console.error('Speech error:', e);
      this.speaking = false;
      this.processQueue();
    };
    
    if (this.speaking) {
      this.queue.push(utterance);
    } else {
      this.synthesis.speak(utterance);
    }
  }
  
  processQueue() {
    if (this.queue.length > 0 && !this.speaking) {
      const next = this.queue.shift();
      this.synthesis.speak(next);
    }
  }
  
  cancel() {
    if (this.synthesis) {
      this.synthesis.cancel();
      this.queue = [];
      this.speaking = false;
    }
  }
  
  // Poker-specific narration methods
  announceCoachSuggestion(coachData) {
    if (!coachData) return;
    
    const { action, reason, confidence, winProb, outs, draws, potOdds, startingStrength, phase, handName, toCall, pot } = coachData;
    
    // Generate insightful commentary based on situation
    let text = this.generateInsightfulCoaching(coachData);
    
    this.speak(text);
  }
  
  generateInsightfulCoaching(data) {
    const { action, reason, confidence, winProb, outs, draws, potOdds, startingStrength, phase, handName, toCall, pot } = data;
    
    // Build contextual insight
    let insight = '';
    
    // Pre-flop insights
    if (phase === 'preflop') {
      if (startingStrength >= 16) {
        insight = `You've got a premium hand here. ${this.getPremiumHandInsight(data)}`;
      } else if (startingStrength >= 12) {
        insight = `This is a solid starting hand. ${this.getStrongHandInsight(data)}`;
      } else if (startingStrength >= 8) {
        insight = `Playable, but be careful. ${this.getMarginalHandInsight(data)}`;
      } else {
        insight = `This hand is weak. ${this.getWeakHandInsight(data)}`;
      }
    }
    // Post-flop insights
    else {
      if (winProb >= 70) {
        insight = `You're in great shape with ${handName || 'a strong hand'}. ${this.getStrongPostFlopInsight(data)}`;
      } else if (winProb >= 50) {
        insight = `Decent spot with ${handName || 'your hand'}. ${this.getMediumPostFlopInsight(data)}`;
      } else if (draws && draws.length > 0 && outs >= 8) {
        insight = `You're drawing here. ${this.getDrawInsight(data)}`;
      } else if (winProb >= 30) {
        insight = `Marginal situation. ${this.getMarginalPostFlopInsight(data)}`;
      } else {
        insight = `Tough spot. ${this.getWeakPostFlopInsight(data)}`;
      }
    }
    
    // Add action recommendation
    const actionText = this.getActionText(action, toCall, pot);
    
    return `${insight} ${actionText}`;
  }
  
  getPremiumHandInsight(data) {
    const insights = [
      "Raise it up and build a pot. You want action with hands like this.",
      "Don't slowplay here. Raise to charge draws and build value.",
      "This is a top tier hand. Raise and isolate weaker holdings.",
      "Put in a raise. You're ahead of most hands people play."
    ];
    return insights[Math.floor(Math.random() * insights.length)];
  }
  
  getStrongHandInsight(data) {
    const insights = [
      "A raise is usually best. You have good equity against calling ranges.",
      "Play this aggressively. Position and initiative matter.",
      "Worth putting in chips. Just be ready to reassess on tough flops."
    ];
    return insights[Math.floor(Math.random() * insights.length)];
  }
  
  getMarginalHandInsight(data) {
    const { toCall, pot, position } = data;
    if (toCall === 0) {
      return "You can see a free flop here. Check and look to improve.";
    } else if (toCall <= pot * 0.2) {
      return "The price is right to take a flop. Call and play post-flop poker.";
    } else {
      return "Too expensive for a speculative hand. Fold and wait for a better spot.";
    }
  }
  
  getWeakHandInsight(data) {
    const { toCall } = data;
    if (toCall === 0) {
      return "Check it down. No need to put chips in with this.";
    }
    return "Save your chips. This hand doesn't have enough equity to continue.";
  }
  
  getStrongPostFlopInsight(data) {
    const { handName, toCall } = data;
    const insights = [
      `${handName} is a monster here. Extract maximum value.`,
      "You've hit big. Bet to build the pot and charge draws.",
      "Strong hand! Consider how to get the most chips in.",
      "You're way ahead. Think about bet sizing for value."
    ];
    return insights[Math.floor(Math.random() * insights.length)];
  }
  
  getMediumPostFlopInsight(data) {
    const { handName, winProb, toCall, pot } = data;
    if (toCall === 0) {
      return `With ${handName}, you can bet for value or check to control the pot size.`;
    } else if (toCall <= pot * 0.5) {
      return `${winProb}% equity is worth a call at this price. You're getting good odds.`;
    } else {
      return `The bet is sizeable. Make sure you're getting the right price to continue.`;
    }
  }
  
  getDrawInsight(data) {
    const { draws, outs, potOdds, toCall, pot } = data;
    const drawType = draws[0]?.type || 'draw';
    const neededEquity = Math.round(100 / (outs * 2));
    const currentOdds = potOdds?.percentage || 0;
    
    if (parseFloat(currentOdds) <= neededEquity * 1.5) {
      return `${drawType} with ${outs} outs. Pot odds are good, around ${neededEquity}% needed to call. The math says continue.`;
    } else {
      return `${drawType} with ${outs} outs, but the pot odds aren't there. You need about ${neededEquity}% equity to call profitably.`;
    }
  }
  
  getMarginalPostFlopInsight(data) {
    const { handName, toCall } = data;
    if (toCall === 0) {
      return `${handName} isn't strong, but you can check for free. See what develops.`;
    }
    return `With ${handName}, you're in a tough spot. Consider if the price is right to continue.`;
  }
  
  getWeakPostFlopInsight(data) {
    const { handName, toCall } = data;
    if (toCall === 0) {
      return `${handName} missed here. Check and hope to improve cheaply.`;
    }
    return `You've got nothing working. Folding saves chips for better opportunities.`;
  }
  
  getActionText(action, toCall, pot) {
    switch (action) {
      case 'fold':
        return "I'd let this one go.";
      case 'check':
        return "Check it through.";
      case 'call':
        return toCall > 0 ? `Calling ${toCall} looks right.` : "Call here.";
      case 'raise':
        return "Put in a raise.";
      case 'all_in':
        return "Ship it! Go all in.";
      default:
        return "";
    }
  }
  
  announcePhase(phase, communityCards = []) {
    const phaseAnnouncements = {
      preflop: [
        'Pre-flop. Time to evaluate your starting hand.',
        'Pre-flop betting. Position and hand strength matter most here.',
        'New hand starting. Check your hole cards.'
      ],
      flop: [
        'The flop is down. Did you connect? Look for pairs, draws, or strong combinations.',
        'Flop dealt. This is where hands take shape. Evaluate your equity.',
        'Here comes the flop. Time to see how your hand developed.'
      ],
      turn: [
        'The turn card. One more to go. Draws need to hit soon.',
        'Turn is out. Pot control becomes important here.',
        'Turn card dealt. Reassess your hand strength and draws.'
      ],
      river: [
        'The river. Final card. No more improving. Play your made hand.',
        'River card. This is it. Make your final decision.',
        'Last card down. What do you have, and what will you do with it?'
      ],
      showdown: [
        'Showdown time! Let\'s see those cards.',
        'Time to show them. Who\'s got the goods?'
      ]
    };
    
    const announcements = phaseAnnouncements[phase];
    if (announcements) {
      const text = announcements[Math.floor(Math.random() * announcements.length)];
      this.speak(text, 'high');
    }
  }
  
  announceWinner(winnerName, handName, pot) {
    const isPlayer = winnerName === 'You';
    
    // Add insight about the winning hand
    const handInsights = {
      'Royal Flush': 'The nuts! The absolute best hand in poker.',
      'Straight Flush': 'An incredibly rare and powerful hand.',
      'Four of a Kind': 'Quads! Almost unbeatable.',
      'Full House': 'A full boat. Very strong holding.',
      'Flush': 'Five of the same suit. Always a solid hand.',
      'Straight': 'Five in a row. A strong made hand.',
      'Three of a Kind': 'Trips. Usually good enough to win.',
      'Two Pair': 'Two pair can be tricky. Often a winner, sometimes second best.',
      'Pair': 'Just a pair, but it was enough this time.',
      'High Card': 'High card wins! Sometimes that\'s all you need.'
    };
    
    const insight = handInsights[handName] || '';
    
    if (isPlayer) {
      const celebrations = [
        `Nice hand! You win ${pot} chips with ${handName}. ${insight}`,
        `Congratulations! ${handName} takes it down. ${pot} chips to you. ${insight}`,
        `You've got it! ${handName} for the win. ${pot} chips added to your stack.`
      ];
      this.speak(celebrations[Math.floor(Math.random() * celebrations.length)], 'high');
    } else {
      const losses = [
        `${winnerName} takes the pot with ${handName}. ${insight}`,
        `${handName} wins for ${winnerName}. ${pot} chips their way. ${insight}`,
        `Pot goes to ${winnerName} showing ${handName}. ${insight}`
      ];
      this.speak(losses[Math.floor(Math.random() * losses.length)], 'high');
    }
  }
  
  announceYourTurn() {
    this.speak("It's your turn to act.");
  }
  
  announceAction(playerName, action, amount) {
    const isPlayer = playerName === 'You';
    const name = isPlayer ? 'You' : playerName;
    
    let text = '';
    switch (action) {
      case 'fold':
        text = isPlayer ? 'You fold.' : `${name} folds.`;
        break;
      case 'check':
        text = isPlayer ? 'You check.' : `${name} checks.`;
        break;
      case 'call':
        text = isPlayer ? `You call ${amount}.` : `${name} calls.`;
        break;
      case 'raise':
        text = isPlayer ? `You raise to ${amount}.` : `${name} raises.`;
        break;
      case 'all_in':
        text = isPlayer ? 'You go all in!' : `${name} is all in!`;
        break;
    }
    
    if (text) {
      this.speak(text);
    }
  }
  
  announceCards(cards, context = 'hole') {
    if (!cards || cards.length === 0) return;
    
    const cardNames = cards.map(c => {
      const rankName = {
        14: 'Ace', 13: 'King', 12: 'Queen', 11: 'Jack', 10: 'Ten',
        9: 'Nine', 8: 'Eight', 7: 'Seven', 6: 'Six', 5: 'Five',
        4: 'Four', 3: 'Three', 2: 'Two'
      };
      
      const suitName = {
        spades: 'spades', hearts: 'hearts',
        diamonds: 'diamonds', clubs: 'clubs'
      };
      
      return `${rankName[c.rank] || c.display} of ${suitName[c.suit] || c.suit}`;
    }).join(', ');
    
    if (context === 'hole') {
      this.speak(`Your cards: ${cardNames}`);
    } else if (context === 'flop') {
      this.speak(`Flop: ${cardNames}`);
    } else if (context === 'turn') {
      this.speak(`Turn: ${cardNames}`);
    } else if (context === 'river') {
      this.speak(`River: ${cardNames}`);
    }
  }
}

// Singleton instance
const narrator = new VoiceNarrator();

export default narrator;
export { VoiceNarrator };
